import { ApolloError, useMutation } from '@apollo/client';
import {
  Body,
  Button,
  FormRadioTileGroup,
  FormTextarea,
  Modal,
  palettes,
  RadioTileGroup,
  useFlashMessage
} from '@indigo-ag/component-module';
import {
  CreditApprovalData,
  CreditApprovalStepDecision,
  CreditApprovalStepStatus,
  CreditApprovalStepType
} from '@indigo-ag/schema';
import { Form, Formik } from 'formik';
import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { faBan, faCheck } from '@fortawesome/pro-regular-svg-icons';

import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import i18next from 'core/i18n';
import { ROLE_TO_STEP_TYPE, RoleNames, text, utils } from 'shared';
import { UserContext } from 'core/components/GateWay/GateWay';
import {
  getCreditApprovalData,
  submitCreditApprovalStepDecision
} from 'features/brazil-grower/queries/creditApproval.queries';

const StyledButtonGroup = styled.div`
  display: flex;
  justify-content: right;
  margin-bottom: 16px;
`;

const StyledModal = styled(Modal)`
  min-width: 50%;
`;

interface Props {
  creditApprovalData?: CreditApprovalData;
}

interface FormShape {
  decision?: CreditApprovalStepDecision;
  comment?: string;
}

export const SubmitApprovalDecision = ({ creditApprovalData }: Props) => {
  /**
   * Context
   */
  const { agFinancingId } = useContext(FinanceProcessContext);
  const { role } = useContext(UserContext);

  /**
   * State
   */
  const [isDecisionOpen, setIsDecisionOpen] = useState(false);
  const [approvalType] = useState<CreditApprovalStepType | undefined>(ROLE_TO_STEP_TYPE[role.id as RoleNames]);
  const [isDecisionButtonDisabled, setIsDecisionButtonDisabled] = useState<boolean>(true);

  /**
   * Mutations
   */
  const [submitCreditApprovalStepDecisionFn] = useMutation(submitCreditApprovalStepDecision);

  const { addFlashMessage } = useFlashMessage();

  // Setting directly to a global variable since the value was not being set to the 'steps' state defined previously.
  const steps = creditApprovalData?.round?.steps;

  useEffect(() => {
    // If no steps but approval type is CREDIT_ANALYST_APPROVAL, enable the decision button
    if (!steps && approvalType === CreditApprovalStepType.CREDIT_ANALYST_APPROVAL) {
      setIsDecisionButtonDisabled(false);
    } else if (!approvalType || !steps) {
      // If no approval type is set (role id not found in mapper) or no steps, disable the decision button
      setIsDecisionButtonDisabled(true);
    } else if (utils.CreditApprovalUtil.isPreviousStepApproved(steps, approvalType)) {
      setIsDecisionButtonDisabled(false);
    } else {
      setIsDecisionButtonDisabled(true);
    }
  }, [approvalType, steps]);

  // No approval type identified based on role
  if (!approvalType) {
    return null;
  }

  // No round or no steps found
  if (!creditApprovalData?.round || !steps?.length) {
    return null;
  }

  // Approval step not required for the round. i.e. Finance Director logged, but only CA decision is required.
  if (steps?.length && !utils.CreditApprovalUtil.getStepByApprovalType(steps, approvalType)) {
    return null;
  }

  // Approval step completed
  if (
    steps?.length &&
    utils.CreditApprovalUtil.getStepByApprovalType(steps, approvalType)?.status === CreditApprovalStepStatus.COMPLETE
  ) {
    return null;
  }

  // Round completed
  if (utils.CreditApprovalUtil.isRoundComplete(creditApprovalData?.round)) {
    return null;
  }

  const openDecisionModal = () => {
    setIsDecisionOpen(true);
  };

  const closeDecisionModal = () => {
    setIsDecisionOpen(false);
  };

  const setDecision = async ({ decision, comment }: FormShape) => {
    if (!decision || !comment || !approvalType) {
      return Promise.reject('decision and comment are required to submit decision');
    }

    if (!approvalType) {
      return Promise.reject('approval type is required to submit decision');
    }

    // No steps or approval step completed
    if (
      !steps?.length ||
      utils.CreditApprovalUtil.getStepByApprovalType(steps, approvalType)?.status === CreditApprovalStepStatus.COMPLETE
    ) {
      return Promise.reject('No steps or approval step completed');
    }

    return submitCreditApprovalStepDecisionFn({
      variables: {
        creditApprovalStepInput: {
          agFinancingId,
          decision: decision,
          decisionNotes: comment,
          stepId: utils.CreditApprovalUtil.getStepByApprovalType(steps, approvalType)?.id,
          stepType: approvalType
        }
      },
      refetchQueries: [
        {
          query: getCreditApprovalData,
          variables: { agFinancingId }
        }
      ],
      awaitRefetchQueries: true
    })
      .then(() => {
        closeDecisionModal();
        addFlashMessage({
          content: '',
          title: i18next.t('decisionSuccessfullyMade'),
          variant: 'success'
        });
      })
      .catch((e: ApolloError) => {
        addFlashMessage({
          content: e.graphQLErrors[0]?.message || i18next.t('error'),
          title: '',
          variant: 'error'
        });
      });
  };

  const content = (
    <>
      <Formik initialValues={{ decision: undefined, comment: '' }} onSubmit={setDecision}>
        {({ isSubmitting, values: { decision, comment } }) => {
          return (
            <Form>
              <FormRadioTileGroup name="decision">
                <RadioTileGroup.Tile
                  icon={faCheck}
                  iconColor={palettes.brand.green.base}
                  value={CreditApprovalStepDecision.APPROVED}
                >
                  <Body>{i18next.t('approved')}</Body>
                </RadioTileGroup.Tile>
                <RadioTileGroup.Tile
                  icon={faBan}
                  iconColor={palettes.brand.red.base}
                  value={CreditApprovalStepDecision.DENIED}
                >
                  <Body>{i18next.t('denied')}</Body>
                </RadioTileGroup.Tile>
              </FormRadioTileGroup>
              <FormTextarea name="comment" required label={i18next.t('notes')} helpText={i18next.t('requiredToSave')} />
              <StyledButtonGroup>
                <Button onClick={closeDecisionModal} variant="secondary">
                  {i18next.t('cancel')}
                </Button>
                <Button busy={isSubmitting} type="submit" disabled={!decision || !comment}>
                  {i18next.t('save')}
                </Button>
              </StyledButtonGroup>
            </Form>
          );
        }}
      </Formik>
    </>
  );

  return (
    <>
      <Button onClick={openDecisionModal} size="small" disabled={isDecisionButtonDisabled}>
        {i18next.t('decision')}
      </Button>
      <StyledModal
        open={isDecisionOpen}
        renderContent={() => content}
        title={text.toTitleCase(i18next.t('creditApprovalDecision'))}
        onRequestClose={closeDecisionModal}
      />
    </>
  );
};
